import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/lockBodyScroll';
import LabProductCarousel from './LabProductCarousel';
import './lab-preview-modal.scss';

const CLOSE_ANIMATION_MS = 220;

function getCloseAnimationMs() {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return 0;
  }

  return CLOSE_ANIMATION_MS;
}

function getCarouselImages(product, displayTitle) {
  if (product?.images?.length > 0) {
    return product.images;
  }

  if (product?.image) {
    return [{ src: product.image, alt: product.imageAlt ?? displayTitle }];
  }

  return [];
}

const LabPreviewModal = ({ test, onClose }) => {
  const product = test.product;
  const visitHref = product?.url ?? test.href;
  const [isOpen, setIsOpen] = useState(false);
  const isClosingRef = useRef(false);
  const closeTimeoutRef = useRef(null);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  const requestClose = useCallback(() => {
    if (isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    setIsOpen(false);
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      isClosingRef.current = false;
      onCloseRef.current();
    }, getCloseAnimationMs());
  }, []);

  const visitPage = useCallback(() => {
    window.open(visitHref, '_blank', 'noopener,noreferrer');
    requestClose();
  }, [visitHref, requestClose]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsOpen(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        requestClose();
      }
    };

    const unlockScroll = lockBodyScroll();
    window.addEventListener('keydown', onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [requestClose]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  if (!test) {
    return null;
  }

  const displayTitle = product?.title ?? test.title;
  const carouselImages = getCarouselImages(product, displayTitle);
  const hasProductDetails = Boolean(
    product?.description || carouselImages.length > 0
  );

  const modalClassName = ['lab-preview-modal', isOpen && 'lab-preview-modal--open']
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div
      className={modalClassName}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-preview-title"
    >
      <button
        type="button"
        className="lab-preview-modal__backdrop"
        aria-label="Close preview"
        onClick={requestClose}
      />
      <div className="lab-preview-modal__panel">
        <header className="lab-preview-modal__header">
          <div className="lab-preview-modal__title-wrap">
            <span className="lab-preview-modal__icon" aria-hidden="true">
              <i className={`fa-solid ${test.icon}`}></i>
            </span>
            <div className="lab-preview-modal__heading">
              <h2 id="lab-preview-title" className="lab-preview-modal__title">
                {displayTitle}
              </h2>
              {product?.subtitle && (
                <p className="lab-preview-modal__subtitle">{product.subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            className="lab-preview-modal__icon-btn"
            aria-label="Close preview"
            onClick={requestClose}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div className="lab-preview-modal__body">
          {!hasProductDetails ? (
            <div className="lab-preview-modal__empty">
              <p>
                Product details are not available right now. Visit equi.life for
                the latest information.
              </p>
            </div>
          ) : (
            <div className="lab-preview-modal__content">
              {carouselImages.length > 0 && (
                <div className="lab-preview-modal__media">
                  <LabProductCarousel
                    images={carouselImages}
                    title={displayTitle}
                  />
                </div>
              )}

              <div className="lab-preview-modal__details">
                <div className="lab-preview-modal__meta">
                  {product.priceFormatted && (
                    <span className="lab-preview-modal__price">
                      {product.priceFormatted}
                    </span>
                  )}
                  {product.reviewCount != null && product.ratingValue != null && (
                    <span className="lab-preview-modal__reviews">
                      <i className="fa-solid fa-star" aria-hidden="true"></i>
                      {product.ratingValue.toFixed(2)} ({product.reviewCount}{' '}
                      {product.reviewCount === 1 ? 'review' : 'reviews'})
                    </span>
                  )}
                </div>

                {product.testTypes?.length > 0 && (
                  <p className="lab-preview-modal__test-type">
                    <span className="lab-preview-modal__test-type-label">
                      Test type
                    </span>
                    {product.testTypes.join(', ')}
                  </p>
                )}

                {(product.productType || product.vendor) && (
                  <p className="lab-preview-modal__type">
                    {[product.productType, product.vendor]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}

                {product.description && (
                  <p className="lab-preview-modal__description">
                    {product.description}
                  </p>
                )}

                {product.highlights?.length > 0 && (
                  <ul className="lab-preview-modal__highlights">
                    {product.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <footer className="lab-preview-modal__footer">
          <button
            type="button"
            className="lab-preview-modal__visit"
            onClick={visitPage}
          >
            Visit page
            <i
              className="fa-solid fa-arrow-up-right-from-square"
              aria-hidden="true"
            ></i>
          </button>
          <button
            type="button"
            className="lab-preview-modal__close"
            onClick={requestClose}
          >
            Close
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default LabPreviewModal;
